import React, { useState, useEffect } from "react";
import {
  Box, Typography, Grid, Card, CardActionArea, CardContent, Avatar,
  Button, TextField, InputAdornment, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress, Alert, Chip
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { db } from "../../../firebase";
import { collection, onSnapshot, addDoc, serverTimestamp, query, orderBy } from "firebase/firestore";
import { useAuth } from "../../../context/AuthContext";
import { useLista } from "../../../hooks/useListe";
import SelectConAggiunta from "../../../components/SelectConAggiunta";
import { leggiIscrittiXlsx, separaCodiciFiscali } from "../../../utils/kpiCorso";
import { salvaCodiciFiscali } from "../../../utils/anagraficaRiservata";
import { salvaLog } from "../../../utils/logger";

export default function CorsiList() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { items: clienti, loading: loadingClienti, aggiungiItem: aggiungiCliente } = useLista("clienti");
  const [corsi, setCorsi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ricerca, setRicerca] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [nome, setNome] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [clienteId, setClienteId] = useState("");
  const [fileIscritti, setFileIscritti] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [errore, setErrore] = useState(null);

  useEffect(() => {
    const q = query(collection(db, "corsi"), orderBy("creatoIl", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setCorsi(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  const clientiMap = Object.fromEntries(clienti.map((c) => [c.id, c.nome]));

  const corsiFiltrati = corsi.filter((c) => {
    const s = ricerca.toLowerCase();
    return c.nome?.toLowerCase().includes(s) || (clientiMap[c.clienteId] || "").toLowerCase().includes(s);
  });

  function chiudiDialog() {
    setDialogOpen(false);
    setNome(""); setDescrizione(""); setClienteId(""); setFileIscritti(null); setErrore(null);
  }

  async function creaCorso() {
    if (!nome.trim() || !clienteId) return;
    setSalvando(true);
    setErrore(null);
    try {
      let iscritti = [];
      let codiciFiscali = {};
      if (fileIscritti) {
        const letti = separaCodiciFiscali(await leggiIscrittiXlsx(fileIscritti));
        codiciFiscali = letti.codiciFiscali;
        // dataIscrizione null = iscritto fin dall'inizio del corso (nessun limite inferiore
        // sulle lezioni conteggiate nei KPI): il roster iniziale spesso documenta un corso
        // già in corso o già concluso, non deve escludere le lezioni già svolte in passato.
        iscritti = letti.righe.map((r) => ({ ...r, dataIscrizione: null, dataRimozione: null }));
      }
      const docRef = await addDoc(collection(db, "corsi"), {
        nome: nome.trim(),
        descrizione: descrizione.trim(),
        clienteId,
        iscritti,
        eventiIds: [],
        creatoDa: currentUser?.email || "",
        creatoIl: serverTimestamp(),
      });
      await salvaCodiciFiscali(docRef.id, codiciFiscali);
      salvaLog(currentUser, "Nuovo corso", docRef.id, nome.trim(), [
        { campo: "Cliente", da: "—", a: clientiMap[clienteId] || clienteId },
        { campo: "Iscritti importati", da: "—", a: String(iscritti.length) },
      ]);
      chiudiDialog();
      navigate(`/utility/kpi-studenti/${docRef.id}`);
    } catch (err) {
      console.error("Errore creazione corso:", err);
      setErrore("Errore durante la creazione del corso. Verifica il file iscritti caricato.");
    }
    setSalvando(false);
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/utility")}
          variant="outlined" size="small">
          Utility
        </Button>
        <Box flexGrow={1}>
          <Typography variant="h5" fontWeight="bold">KPI Studenti</Typography>
          <Typography variant="body2" color="text.secondary">
            KPI di presenza e apprendimento sui corsi di formazione, per lo staff e per i clienti.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Nuovo corso
        </Button>
      </Box>

      <TextField fullWidth placeholder="Cerca corso per nome o cliente..."
        value={ricerca} onChange={(e) => setRicerca(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
        sx={{ mb: 3 }} size="small" />

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : corsiFiltrati.length === 0 ? (
        <Typography variant="body2" color="text.secondary" textAlign="center" mt={4}>
          Nessun corso trovato. Crea il primo corso per iniziare a tracciare i KPI degli studenti.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {corsiFiltrati.map((c) => (
            <Grid item xs={12} sm={6} md={4} key={c.id}>
              <Card elevation={1}>
                <CardActionArea onClick={() => navigate(`/utility/kpi-studenti/${c.id}`)} sx={{ p: 2, height: "100%" }}>
                  <CardContent sx={{ p: 0 }}>
                    <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                      <Avatar sx={{ backgroundColor: "#E6F1FB", color: "primary.main" }}>
                        <SchoolIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">{c.nome}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {clientiMap[c.clienteId] || "—"}
                        </Typography>
                      </Box>
                    </Box>
                    <Box display="flex" gap={1}>
                      <Chip size="small" label={`${(c.iscritti || []).length} iscritti`} />
                      <Chip size="small" label={`${(c.eventiIds || []).length} lezioni`} />
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={dialogOpen} onClose={chiudiDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Nuovo corso</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Nome corso" value={nome} onChange={(e) => setNome(e.target.value)} required fullWidth autoFocus />
            <TextField label="Descrizione" value={descrizione} onChange={(e) => setDescrizione(e.target.value)}
              multiline rows={2} fullWidth />
            <SelectConAggiunta label="Cliente" value={clienteId} onChange={setClienteId}
              items={clienti} loading={loadingClienti} onAggiungi={aggiungiCliente} required />
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" mb={1}>Elenco iscritti (opzionale)</Typography>
              <Button variant="outlined" component="label" fullWidth
                startIcon={<UploadFileIcon />} color={fileIscritti ? "success" : "primary"}>
                {fileIscritti ? fileIscritti.name : "Seleziona XLSX iscritti"}
                <input type="file" accept=".xlsx,.xls" hidden
                  onChange={(e) => setFileIscritti(e.target.files[0] || null)} />
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                Puoi anche saltare questo passaggio e aggiungere gli iscritti in un secondo momento.
              </Typography>
            </Box>
            {errore && <Alert severity="error">{errore}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={chiudiDialog}>Annulla</Button>
          <Button variant="contained" onClick={creaCorso} disabled={salvando || !nome.trim() || !clienteId}>
            {salvando ? <CircularProgress size={20} color="inherit" /> : "Crea corso"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
